/**
 * Helper functions for building Footer component props
 */
import { IntlShape } from 'react-intl';
// eslint-disable-next-line import/no-unresolved
import { Content } from '@plone/types';
import {
  LanguageItemProps,
  FooterProps as DSFooterProps,
} from '@designsystem/stories/04-organisms/Footer';
import { NavItem } from '@fhnw/types/navItem';
import { normalizeNavigation } from '@fhnw/helpers/DesignSystem/normalizeNavigation';
import { normalizeLink } from '@fhnw/helpers/DesignSystem/normalizeContent';
import { buildLanguageItems } from '@fhnw/helpers/DesignSystem/buildLanguages';
import { FooterMessages as messages } from './FooterMessages';

interface LanguageItem {
  ['@id']: string;
  language: string;
}

interface NavContent extends Omit<Content, '@components'> {
  '@components'?: {
    meta?: NavItem;
    footer?: NavItem;
    newsletter?: NavItem;
    translations?: { items: LanguageItem[] };
    university?: Content;
  };
}

/**
 * Builds the languages array for the footer, current language first
 *
 * @param content - Navigation content from Plone
 * @returns Array of language items
 */
export const buildLanguages = (content?: NavContent): LanguageItemProps[] =>
  buildLanguageItems(content, { activeFirst: true });

/**
 * Builds the newsletter CTA button props
 *
 * @param intl - React Intl instance
 * @param newsletter - Newsletter navigation item
 * @returns Button props for newsletter signup
 */
export const buildNewsletterButton = (
  intl: IntlShape,
  newsletter: NavItem,
) => ({
  href: normalizeLink(newsletter.remoteUrl),
  label: intl.formatMessage(messages.SignUpLabel),
  type: 'primary' as const,
  icon: 'arrow_right_triangle' as const,
  icon_after: true,
});

/**
 * Builds the complete footer props object
 *
 * @param intl - React Intl instance
 * @param content - Navigation content from Plone
 * @param filteredLogos - Filtered array of logo props
 * @returns Complete footer props
 */
export const buildFooterArgs = (
  intl: IntlShape,
  content: NavContent | undefined,
  filteredLogos: DSFooterProps['logos'],
): DSFooterProps => {
  const { meta, footer, newsletter, university } =
    content?.['@components'] || {};

  const args: DSFooterProps = {
    title: intl.formatMessage(messages.Title),
    totop: intl.formatMessage(messages.ToTop),
    items: normalizeNavigation(footer),
    logos: filteredLogos,
    colophon: intl.formatMessage(messages.Copyright),
    colophon_items: normalizeNavigation(meta),
    languages: buildLanguages(content),
  };

  if (university) {
    args.subtitle = args.title;
    args.title = university?.teaser_title || university?.title;
  }

  if (newsletter) {
    args.cta_text = intl.formatMessage(messages.SignUpMessage);
    args.cta_button = buildNewsletterButton(intl, newsletter);
  }

  return args;
};
